import {createReducer} from '@reduxjs/toolkit';
import keyBy from 'lodash/keyBy';

import {setAreaCoverage} from '@explorer/actions/area-coverage';
import {
  addItemsToOrder,
  addItemsToView,
  addResultToOrder,
  addResultToView,
  handleResults,
  removeAllFromOrder,
  removeAllFromView,
  removeItemsFromOrder,
  removeItemsFromView,
  removeResultFromOrder,
  removeResultFromView,
  resetResults,
  resultsCompleted,
  selectResults,
  updateStatus,
} from '@explorer/actions/results';
import {clearSearch} from '@explorer/actions/search';
import {resetTour, setTour} from '@explorer/actions/tour';
import {TOURS} from '@explorer/constants';
import {filterByAreaCoverage} from '@explorer/selectors/results';
import {getUniqueId} from '@explorer/util/items';

export const initialState = {
  resultsById: {},
  resultsIds: [],
  complete: false,
  loading: null,

  orderResults: [],
  viewResults: [],
  orderItems: [],
  viewItems: [],
};

const results = createReducer(initialState, builder => {
  builder.addCase(resetResults, () => initialState);
  builder.addCase(clearSearch, () => initialState);

  builder.addCase(resultsCompleted, state => {
    state.complete = true;
  });

  builder.addCase(updateStatus, (state, {payload}) => {
    state.loading = payload;
  });

  builder.addCase(handleResults, (state, {payload: results}) => {
    const ids = results.map(result => result.id);
    const updatedResults = keyBy(results, 'id');

    state.resultsIds = ids;

    state.resultsById = {
      ...state.resultsById,
      ...updatedResults,
    };

    // Select results if corresponding items are already selected, as is the
    // case for refreshing or hitting a url with items in the hash.
    const viewResults = results
      .filter(result =>
        result.properties.itemIds.some(id => state.viewItems.includes(id))
      )
      .map(r => r.id);

    state.viewResults = Array.from(
      new Set([...state.viewResults, ...viewResults])
    );

    const orderResults = results
      .filter(result =>
        result.properties.itemIds.some(id => state.orderItems.includes(id))
      )
      .map(r => r.id);

    state.orderResults = Array.from(
      new Set([...state.orderResults, ...orderResults])
    );
  });

  builder.addCase(addItemsToOrder, (state, {payload: {resultId, items}}) => {
    if (resultId && !state.orderResults.includes(resultId)) {
      state.orderResults = [...state.orderResults, resultId];
    }

    state.orderItems = [...state.orderItems, ...items];
  });

  builder.addCase(addItemsToView, (state, {payload: {resultId, items}}) => {
    if (resultId && !state.viewResults.includes(resultId)) {
      state.viewResults = [...state.viewResults, resultId];
    }
    state.viewItems = [...state.viewItems, ...items];
  });

  builder.addCase(
    removeItemsFromOrder,
    (state, {payload: {resultId, items}}) => {
      const newItems = state.orderItems.filter(
        orderItem => !items.includes(orderItem)
      );

      const resultItemIds =
        state.resultsById[resultId]?.properties.itemIds ?? [];

      // check if there are ANY items from the result left
      const hasAny = resultItemIds.some(itemId => newItems.includes(itemId));
      if (!hasAny) {
        const newResults = state.orderResults.filter(
          orderResult => orderResult !== resultId
        );
        state.orderResults = newResults;
      }

      state.orderItems = newItems;
    }
  );

  builder.addCase(
    removeItemsFromView,
    (state, {payload: {resultId, items}}) => {
      const newItems = state.viewItems.filter(
        viewItem => !items.includes(viewItem)
      );

      const resultItemIds =
        state.resultsById[resultId]?.properties.itemIds ?? [];

      // check if there are ANY items from the result left
      const hasAll = resultItemIds.some(itemId => newItems.includes(itemId));
      if (!hasAll) {
        const newResults = state.viewResults.filter(
          orderResult => orderResult !== resultId
        );
        state.viewResults = newResults;
      }

      state.viewItems = newItems;
    }
  );

  builder.addCase(addResultToOrder, (state, {payload: {reset, resultId}}) => {
    // only use items the user has download access to
    const resultItems = state.resultsById[resultId]
      ? state.resultsById[resultId].properties.items
          .filter(item => item.properties.canDownload)
          .map(item => getUniqueId(item))
      : [];

    const resultCount = resultItems.length;
    if (reset) {
      state.orderResults = resultCount ? [resultId] : [];
      state.orderItems = resultCount ? [...resultItems] : [];
    } else {
      state.orderResults = resultCount
        ? [...state.orderResults, resultId]
        : state.orderResults;
      state.orderItems = resultCount
        ? Array.from(new Set([...state.orderItems, ...resultItems]))
        : state.orderItems;
    }
  });

  builder.addCase(addResultToView, (state, {payload: {reset, resultId}}) => {
    const resultItems = state.resultsById[resultId]?.properties.itemIds || [];

    if (reset) {
      state.viewResults = [resultId];
      state.viewItems = [...resultItems];
    } else {
      state.viewResults = [...state.viewResults, resultId];
      state.viewItems = Array.from(
        new Set([...state.viewItems, ...resultItems])
      );
    }
  });

  builder.addCase(removeResultFromView, (state, {payload: {resultId}}) => {
    const resultItems = state.resultsById[resultId]?.properties.itemIds || [];

    state.viewResults = state.viewResults.filter(
      viewResult => viewResult !== resultId
    );

    state.viewItems = state.viewItems.filter(
      viewItem => !resultItems.includes(viewItem)
    );
  });

  builder.addCase(removeAllFromView, state => {
    state.viewResults = [];
    state.viewItems = [];
  });

  builder.addCase(removeResultFromOrder, (state, {payload: {resultId}}) => {
    const resultItems = state.resultsById[resultId]?.properties.itemIds || [];

    state.orderResults = state.orderResults.filter(
      orderItem => orderItem !== resultId
    );

    state.orderItems = state.orderItems.filter(
      orderItem => !resultItems.includes(orderItem)
    );
  });

  builder.addCase(removeAllFromOrder, state => {
    state.orderItems = [];
    state.orderResults = [];
  });

  builder.addCase(setAreaCoverage, (state, {payload: areaCoverage}) => {
    const selectedViewResults = state.viewResults.map(
      resultId => state.resultsById[resultId]
    );
    const filteredViewResults = filterByAreaCoverage(
      selectedViewResults,
      areaCoverage
    );

    const allViewItems = filteredViewResults
      .map(result => result.properties.itemIds)
      .flat();

    const filteredViewItems = state.viewItems.filter(viewItem =>
      allViewItems.includes(viewItem)
    );

    state.viewResults = filteredViewResults.map(result => result.id);
    state.viewItems = filteredViewItems;

    const selectedOrderResults = state.orderResults.map(
      resultId => state.resultsById[resultId]
    );
    const filteredOrderResults = filterByAreaCoverage(
      selectedOrderResults,
      areaCoverage
    );

    const allOrderItems = filteredOrderResults
      .map(result => result.properties.itemIds)
      .flat();

    const filteredOrderItems = state.orderItems.filter(orderItem =>
      allOrderItems.includes(orderItem)
    );

    state.orderResults = filteredOrderResults.map(result => result.id);
    state.orderItems = filteredOrderItems;
  });

  builder.addCase(selectResults, (state, {payload: {resultIds}}) => {
    const resultItems = resultIds
      .map(resultId => state.resultsById[resultId]?.properties.itemIds ?? [])
      .flat();

    state.viewResults = [...state.viewResults, ...resultIds];
    state.viewItems = [...state.viewItems, ...resultItems];

    state.orderResults = [...state.orderResults, ...resultIds];
    state.orderItems = [...state.orderItems, ...resultItems];
  });

  builder.addCase(setTour, (state, {payload}) => {
    if (payload !== TOURS.OVERZOOM) {
      return initialState;
    }
    return state;
  });

  builder.addCase(resetTour, (state, {payload}) => {
    if (payload !== TOURS.OVERZOOM) {
      return initialState;
    }
    return state;
  });
});

export default results;
